import { z } from "zod";
import { HostnameRegex, Ipv4Regex, StringArray } from "./common.ts";
import {
	CountryRegionsSchema,
	HostRegionSchema,
	RegionFallbacksSchema,
} from "./geo.ts";
import {
	isReservedWorkerName,
	RESERVED_WORKER_NAME_MESSAGE,
} from "./provision.ts";

/**
 * Hostname of a worker being registered or edited.
 *
 * Same reserved-name refusal as a freshly deployed worker: the name is the
 * `/api/workers/:hostname` key either way, so `sessions` would shadow a route
 * regardless of how the row got created.
 */
const WorkerHostnameSchema = z
	.string()
	.regex(HostnameRegex, "invalid hostname")
	.max(64)
	.refine((v) => !isReservedWorkerName(v), RESERVED_WORKER_NAME_MESSAGE);

// URL path prefix on the LB in front of /fleet/* (e.g. "/bb"). Empty string =
// local-api on root. Validation: empty, or starts with /, contains no ?#, and
// does not end with / (so concatenation with /fleet/... is unambiguous).
const BasePathSchema = z
	.string()
	.max(128)
	.regex(
		/^(|\/[^/?#][^?#]*)$/,
		"must be empty or start with / and contain no ?#",
	)
	.refine((v) => v === "" || !v.endsWith("/"), "must not end with /");

/**
 * Singleton cluster patch. All fields optional; only provided ones are
 * applied. `name` is required on first creation but the same endpoint covers
 * initial seeding, so the required-on-create check is done in the route
 * handler rather than in the schema.
 */
export const ClusterPatchSchema = z
	.object({
		name: z.string().min(1).max(64).optional(),
		publicHostnames: StringArray.optional(),
		releaseHostname: z.string().min(1).nullable().optional(),
		managerHostname: z.string().min(1).nullable().optional(),
		webroot: z.string().min(1).optional(),
		extraAllowedIps: z
			.array(z.string().regex(Ipv4Regex, "must be IPv4 a.b.c.d"))
			.max(64)
			.optional(),
		lbPolicy: z.enum(["round-robin", "least-conn"]).optional(),
		basicAuthRealm: z.string().min(1).nullable().optional(),
		basicAuthFile: z.string().min(1).nullable().optional(),
		// Cloudflare edge sub-model toggles. tlsMode itself is only changed
		// via POST /api/system/tls (which also writes certs + re-renders);
		// these let the settings UI stage the Cloudflare sub-choices.
		cfMode: z.enum(["flexible", "full"]).optional(),
		cfFullCertSource: z.enum(["self-signed", "origin-ca"]).optional(),
		cfOriginLock: z.boolean().optional(),
		originMtlsEnabled: z.boolean().optional(),
		originMtlsCaPath: z.string().min(1).nullable().optional(),
		regionFallbacks: RegionFallbacksSchema.optional(),
		countryRegions: CountryRegionsSchema.optional(),
		maxConcurrentLaunches: z.number().int().min(1).max(100).optional(),
	})
	.strict();
export type ClusterPatchInput = z.infer<typeof ClusterPatchSchema>;

/**
 * Updater-agent wiring for a worker on another host: cluster-manager calls the
 * agent to drain, pull and recreate the container. Only remote hosts need it —
 * same-box workers are rolled directly over the docker socket.
 *
 * The token is write-only. Reads expose `hasUpdaterAgent` instead, so a secret
 * never travels back to the browser.
 */
const UpdaterAgentFields = {
	updaterAgentUrl: z
		.string()
		.url("must be a URL, e.g. http://10.0.0.5:3999")
		.max(512)
		.nullable()
		.optional(),
	updaterAgentToken: z.string().max(512).nullable().optional(),
} as const;

/**
 * Where a worker runs. Editable because the column was backfilled from a
 * heuristic (a socket-managed container is on this box, anything else is not),
 * which is right for every row that existed at the time and is still a guess —
 * a hand-registered worker on the operator's own machine would be called
 * remote, and only they can correct that.
 */
export const WorkerPlacementSchema = z.enum(["local", "remote"]);
export type WorkerPlacement = z.infer<typeof WorkerPlacementSchema>;

export const FleetHostCreateSchema = z
	.object({
		hostname: WorkerHostnameSchema,
		publicIp: z.string().regex(Ipv4Regex, "must be IPv4 a.b.c.d"),
		lbPort: z.number().int().min(1).max(65535),
		weight: z.number().int().min(1).max(1000),
		enabled: z.boolean(),
		region: HostRegionSchema,
		apiKey: z.string().max(512).nullable().optional(),
		statusEndpoint: z.string().min(1).max(512).optional(),
		basePath: BasePathSchema.optional(),
		probePort: z.number().int().min(1).max(65535).optional(),
		...UpdaterAgentFields,
		// Optional rather than defaulted, so this schema's input and output types
		// stay the same shape: it is also the resolver behind the worker form, and
		// a field that is optional to send but guaranteed on the way out makes the
		// form's values type disagree with itself. The route applies the default,
		// which for a hand-registered worker is `remote` — the operator is telling
		// us about a machine we did not deploy to.
		placement: WorkerPlacementSchema.optional(),
		notes: z.string().max(512).nullable().optional(),
	})
	.strict();
export type FleetHostCreateInput = z.infer<typeof FleetHostCreateSchema>;

export const FleetHostPatchSchema = z
	.object({
		hostname: WorkerHostnameSchema.optional(),
		publicIp: z.string().regex(Ipv4Regex).optional(),
		lbPort: z.number().int().min(1).max(65535).optional(),
		weight: z.number().int().min(1).max(1000).optional(),
		enabled: z.boolean().optional(),
		region: HostRegionSchema,
		apiKey: z.string().max(512).nullable().optional(),
		statusEndpoint: z.string().min(1).max(512).optional(),
		basePath: BasePathSchema.optional(),
		probePort: z.number().int().min(1).max(65535).optional(),
		...UpdaterAgentFields,
		placement: WorkerPlacementSchema.optional(),
		notes: z.string().max(512).nullable().optional(),
	})
	.strict();
export type FleetHostPatchInput = z.infer<typeof FleetHostPatchSchema>;
